/** CSV modal — template name, color, save/update. For Media Devoted | Created By Ciro Autiero */
import type { FC } from 'react';

import { Button, ColorPicker, Input } from '@/components/ui';
import { cn } from '@/lib/utils';

interface CsvExportTemplatesSaveRowProps {
  fieldLocked: boolean;
  canUpdate: boolean;
  canManageTemplates: boolean;
  templateName: string;
  templateColor: string;
  saveAsDefault: boolean;
  onTemplateNameChange: (v: string) => void;
  onTemplateColorChange: (v: string) => void;
  onSaveAsDefaultChange: (v: boolean) => void;
  onSaveTemplate: () => void;
  onUpdateTemplate: () => void;
}

export const CsvExportTemplatesSaveRow: FC<CsvExportTemplatesSaveRowProps> = ({
  fieldLocked,
  canUpdate,
  canManageTemplates,
  templateName,
  templateColor,
  saveAsDefault,
  onTemplateNameChange,
  onTemplateColorChange,
  onSaveAsDefaultChange,
  onSaveTemplate,
  onUpdateTemplate,
}) => (
  <div className="space-y-2 border-t border-border/30 pt-3">
    <p className="text-xs font-medium text-muted-foreground">
      Save current columns
      {!canManageTemplates ? ' (read-only — no edit permission)' : ''}
    </p>
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex min-w-[140px] flex-1 flex-col gap-1">
        <label
          htmlFor="csv-export-template-name"
          className="text-xs font-medium text-muted-foreground"
        >
          Template name
        </label>
        <Input
          id="csv-export-template-name"
          name="csvExportTemplateName"
          placeholder="Template name…"
          value={templateName}
          disabled={fieldLocked}
          onChange={(e) => onTemplateNameChange(e.target.value)}
          className="min-w-0"
          autoComplete="off"
        />
      </div>
      <div
        className={cn(
          'flex flex-col gap-1',
          fieldLocked && 'pointer-events-none opacity-50',
        )}
      >
        <label
          htmlFor="csv-export-template-color-trigger"
          className="text-xs font-medium text-muted-foreground"
        >
          Color
        </label>
        <ColorPicker
          triggerId="csv-export-template-color-trigger"
          hexInputId="csv-export-template-color-hex"
          hexInputName="csvExportTemplateColor"
          color={templateColor}
          onChange={onTemplateColorChange}
          placeholder="#3b82f6"
          className="h-9"
        />
      </div>
      <Button variant="secondary" size="sm" onClick={onUpdateTemplate} disabled={!canUpdate}>
        Update template
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onSaveTemplate}
        disabled={!templateName.trim() || fieldLocked}
      >
        Save as new
      </Button>
    </div>
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <input
        id="csv-export-template-default"
        name="csvExportTemplateDefault"
        type="checkbox"
        checked={saveAsDefault}
        disabled={fieldLocked}
        onChange={(e) => onSaveAsDefaultChange(e.target.checked)}
      />
      Set as default for this tab
    </label>
  </div>
);
